import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { eid } from '../../utils/entityId';
import GameLayout from '../layout/GameLayout';
import ConfirmModal from '../ui/ConfirmModal';
import dropInService from '../../services/api/dropInService';
import paymentService from '../../services/api/paymentService';
import { RefundPolicyNote, leaveMessage, HostPaymentsLink } from '../payments/RefundPolicy';
import PaidSummary from '../payments/PaidSummary';
import { formatMoney } from '../../utils/money';
import '../../styles/features/dropIn.css';
import { eventTimeZone, formatEventTimeForViewer } from '../../utils/eventTime';

/* ── Helpers ─────────────────────────────────────────────────────── */
/** At the venue's local time (utils/eventTime) */
const fmtDate = (iso, timeZone) => {
  if (!iso) return '—';
  return formatEventTimeForViewer(iso, timeZone, {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const initials = (name = '') =>
  name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) || '?';

// How often an open chat tab fetches new messages
const MESSAGE_POLL_MS = 8000;

const TEAM_COLORS = ['#a78bfa', '#38bdf8', '#4ade80', '#fb923c', '#f472b6', '#facc15', '#22d3ee', '#a3e635'];

/**
 * "Bibs 2-1 Skins" for a drop-in match: team names from its stored rosters,
 * and the score once there is one (DI-B13).
 */
export const matchLine = (m) => {
  const teams = m.drop_in_teams || m.match_config?.drop_in_teams || {};
  const a = teams.team_a?.name || m.participant_1_name || 'Team A';
  const b = teams.team_b?.name || m.participant_2_name || 'Team B';
  const sd = m.score_data || {};
  const num = (v) => (typeof v === 'number' ? v : typeof v?.total === 'number' ? v.total : null);
  let s1 = num(sd.participant1);
  let s2 = num(sd.participant2);
  if ((s1 === null || s2 === null) && Array.isArray(sd.games) && sd.games.length) {
    s1 = sd.games.filter((g) => g[0] > g[1]).length;
    s2 = sd.games.filter((g) => g[1] > g[0]).length;
  }
  return m.status === 'completed' && s1 !== null && s2 !== null ? `${a} ${s1}-${s2} ${b}` : `${a} vs ${b}`;
};

/* ── Teams generation modal ──────────────────────────────────────── */
const TeamsModal = ({ teams: initialTeams, numTeams, onShuffle, onCreateMatch, onClose, loading }) => {
  const [teams, setTeams]           = useState(() => initialTeams.map((t) => ({ ...t, players: [...t.players] })));
  const [names, setNames]           = useState(() => Object.fromEntries(initialTeams.map((t) => [t.slot, t.name])));
  const [editingSlot, setEditingSlot] = useState(null);
  const [dragging, setDragging]     = useState(null); // { userId, fromSlot }
  const [dragOver, setDragOver]     = useState(null); // slot number

  // Sync when parent shuffles (new initialTeams prop)
  useEffect(() => {
    setTeams(initialTeams.map((t) => ({ ...t, players: [...t.players] })));
    setNames(Object.fromEntries(initialTeams.map((t) => [t.slot, t.name])));
  }, [initialTeams]);

  const handleDragStart = (e, userId, fromSlot) => {
    setDragging({ userId, fromSlot });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDrop = (e, toSlot) => {
    e.preventDefault();
    setDragOver(null);
    if (!dragging || dragging.fromSlot === toSlot) { setDragging(null); return; }

    setTeams((prev) => {
      const next = prev.map((t) => ({ ...t, players: [...t.players] }));
      const fromTeam = next.find((t) => t.slot === dragging.fromSlot);
      const toTeam   = next.find((t) => t.slot === toSlot);
      const playerIdx = fromTeam.players.findIndex((p) => p.user_id === dragging.userId);
      if (playerIdx === -1) return prev;
      const [player] = fromTeam.players.splice(playerIdx, 1);
      toTeam.players.push(player);
      return next;
    });
    setDragging(null);
  };

  const mergedTeams = teams.map((t) => ({ ...t, name: names[t.slot] ?? t.name }));

  return (
    <div className="dropin-teams-modal-overlay" onClick={onClose}>
      <div className="dropin-teams-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="dropin-teams-modal__title">🎲 Generated Teams</h2>
        <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.8rem', marginBottom: '1.25rem' }}>
          Drag players between teams to swap them. Click a team name to rename it.
        </p>

        <div className="dropin-teams-grid">
          {mergedTeams.map((team, idx) => (
            <div
              key={team.slot}
              className={`dropin-team-col${dragOver === team.slot ? ' dropin-team-col--dropzone' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(team.slot); }}
              onDragLeave={() => setDragOver(null)}
              onDrop={(e) => handleDrop(e, team.slot)}
            >
              {/* Editable team name */}
              {editingSlot === team.slot ? (
                <input
                  autoFocus
                  className="dropin-team-name-input"
                  style={{ color: TEAM_COLORS[idx % TEAM_COLORS.length] }}
                  value={names[team.slot] ?? team.name}
                  onChange={(e) => setNames((n) => ({ ...n, [team.slot]: e.target.value }))}
                  onBlur={() => setEditingSlot(null)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setEditingSlot(null); }}
                />
              ) : (
                <div
                  className="dropin-team-col__name dropin-team-col__name--editable"
                  style={{ color: TEAM_COLORS[idx % TEAM_COLORS.length] }}
                  onClick={() => setEditingSlot(team.slot)}
                  title="Click to rename"
                >
                  {names[team.slot] ?? team.name} ✏️
                </div>
              )}

              {/* Player count badge */}
              <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', textAlign: 'center', marginBottom: '0.5rem' }}>
                {team.players.length} player{team.players.length !== 1 ? 's' : ''}
              </div>

              {/* Players */}
              {team.players.length === 0 && (
                <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '0.5rem 0' }}>
                  Drop a player here
                </div>
              )}
              {team.players.map((p) => {
                const name = p.display_name || `${p.first_name} ${p.last_name}`;
                return (
                  <div
                    key={p.user_id}
                    className={`dropin-team-player dropin-team-player--draggable${dragging?.userId === p.user_id ? ' dragging' : ''}`}
                    draggable
                    onDragStart={(e) => handleDragStart(e, p.user_id, team.slot)}
                    onDragEnd={() => setDragging(null)}
                    title="Drag to move to another team"
                  >
                    <div
                      className="dropin-team-player__avatar"
                      style={{ background: `linear-gradient(135deg, ${TEAM_COLORS[idx % TEAM_COLORS.length]}, #0f172a)` }}
                    >
                      {initials(name)}
                    </div>
                    <span style={{ flex: 1 }}>{name}</span>
                    <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.3)', cursor: 'grab' }}>⠿</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div className="dropin-teams-modal__footer">
          <button className="dropin-btn dropin-btn--secondary" onClick={onClose} style={{ flex: 1 }}>
            Close
          </button>
          <button
            className="dropin-btn dropin-btn--secondary"
            onClick={onShuffle}
            disabled={loading}
            style={{ flex: 1 }}
          >
            🔀 Shuffle
          </button>
          <button
            className="dropin-btn dropin-btn--success"
            onClick={() => onCreateMatch(mergedTeams)}
            disabled={loading}
            style={{ flex: 1 }}
          >
            ⚡ Create Match
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main component ───────────────────────────────────────────────── */
const DropInDetails = ({ token, user, onLogout }) => {
  const { id }    = useParams();
  const navigate  = useNavigate();

  const [dropIn, setDropIn]           = useState(null);
  const [loading, setLoading]         = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError]             = useState(null);
  const [actionMsg, setActionMsg]     = useState(null);
  const [numTeams, setNumTeams]             = useState(2);
  const [activeTab, setActiveTab]           = useState('players');
  // Inline teams tab state
  const [inlineTeams, setInlineTeams]       = useState(null);
  const [inlineNames, setInlineNames]       = useState({});
  const [inlineEditSlot, setInlineEditSlot] = useState(null);
  const [inlineDragging, setInlineDragging] = useState(null);
  const [inlineDragOver, setInlineDragOver] = useState(null);
  const [inlineBench, setInlineBench]       = useState([]);
  const [confirmedTeams, setConfirmedTeams] = useState(null);
  const [matchSetups, setMatchSetups]       = useState([]);
  const [dropInMatches, setDropInMatches]   = useState([]);
  const [matchesLoading, setMatchesLoading] = useState(false);

  // Restore confirmed-teams / match-setups from localStorage so navigation doesn't wipe them
  useEffect(() => {
    if (!id) return;
    const saved = localStorage.getItem(`dropin_teams_${id}`);
    if (!saved) return;
    try {
      const { ct, ms } = JSON.parse(saved);
      if (ct) setConfirmedTeams(ct);
      if (ms) setMatchSetups(ms);
    } catch { /* ignore */ }
  }, [id]);

  useEffect(() => {
    if (!id) return;
    if (confirmedTeams) {
      localStorage.setItem(`dropin_teams_${id}`, JSON.stringify({ ct: confirmedTeams, ms: matchSetups }));
    } else {
      localStorage.removeItem(`dropin_teams_${id}`);
    }
  }, [id, confirmedTeams, matchSetups]);

  // Sync suggested team count when session data loads/changes
  useEffect(() => {
    if (!dropIn) return;
    const ps    = dropIn.players || [];
    const active = ps.filter((p) => p.status === 'confirmed' || p.status === 'checked_in').length;
    const ppt    = dropIn.playersPerTeam ?? dropIn.players_per_team ?? 6;
    const maxValid = active >= 2 ? Math.max(2, Math.min(8, Math.floor(active / ppt) || 2)) : 2;
    setNumTeams(maxValid);
  }, [dropIn]);
  const [inviteSearch, setInviteSearch] = useState('');
  const [inviteResults, setInviteResults] = useState([]);
  const [inviteSearching, setInviteSearching] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState({ isOpen: false, playerId: null, playerName: '' });
  const [leaveOpen, setLeaveOpen] = useState(false);

  // Messages
  const [messages, setMessages]                 = useState([]);
  const [messagesLoading, setMessagesLoading]   = useState(false);
  const [messageInput, setMessageInput]         = useState('');
  const [messageSending, setMessageSending]     = useState(false);

  // Permanent members (private recurring drop-ins)
  const [members, setMembers]                   = useState([]);
  const [membersLoading, setMembersLoading]     = useState(false);
  const [memberSearch, setMemberSearch]         = useState('');
  const [memberResults, setMemberResults]       = useState([]);
  const [memberSearching, setMemberSearching]   = useState(false);

  // Recurring: opening a new weekly session
  const [weeklyOpening, setWeeklyOpening]       = useState(false);

  const userId = user?.id;
  const [paymentNote, setPaymentNote] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res  = await dropInService.getById(id);
      setDropIn(res.data.dropIn);
    } catch (err) {
      setError(err.message || 'Failed to load drop-in');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Back from Stripe Checkout. Cancelled: close it now so the held seat is
  // freed at once (the server checks it is ours). Paid: Stripe tells the
  // server by webhook, which can take a few seconds, so look again shortly.
  const [searchParams, setSearchParams] = useSearchParams();
  const paymentReturn = searchParams.get('payment');
  const paymentId = searchParams.get('id');
  useEffect(() => {
    if (!paymentReturn) return undefined;
    let timer;
    setSearchParams({}, { replace: true });
    if (paymentReturn === 'cancelled' && paymentId) {
      paymentService.cancelPayment(paymentId)
        .catch(() => {})
        .finally(() => {
          setPaymentNote('Payment cancelled: your spot was not kept.');
          load();
        });
    } else if (paymentReturn === 'success') {
      setPaymentNote('Payment received: your spot is confirmed.');
      timer = setTimeout(load, 3000);
    }
    return () => clearTimeout(timer);
  }, [paymentReturn, paymentId, setSearchParams, load]);

  // Load matches when tab becomes active
  useEffect(() => {
    if ((activeTab !== 'matches' && activeTab !== 'standings') || !id) return;
    setMatchesLoading(true);
    dropInService.getMatches(id)
      .then((res) => setDropInMatches(res.data?.matches || []))
      .catch(() => {})
      .finally(() => setMatchesLoading(false));
  }, [activeTab, id]);

  // Load messages when the messages tab is active, then keep them fresh
  // while it stays open (DI-B19: chat used to be fetched once).
  useEffect(() => {
    if (activeTab !== 'messages' || !id) return undefined;
    let cancelled = false;
    const fetchMessages = (first) => {
      if (first) setMessagesLoading(true);
      return dropInService.getMessages(id)
        .then((res) => { if (!cancelled) setMessages(res.data?.messages || []); })
        .catch(() => {})
        .finally(() => { if (first && !cancelled) setMessagesLoading(false); });
    };
    fetchMessages(true);
    const timer = setInterval(() => fetchMessages(false), MESSAGE_POLL_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [activeTab, id]);

  // Load permanent members for private drop-ins
  useEffect(() => {
    if (!dropIn || dropIn.visibility !== 'private' || !userId) return;
    const hostId = dropIn.hostId || dropIn.host_id;
    if (hostId !== userId) return;
    setMembersLoading(true);
    dropInService.getMembers(id)
      .then((res) => setMembers(res.data?.members || []))
      .catch(() => {})
      .finally(() => setMembersLoading(false));
  }, [dropIn, userId, id]);

  if (loading) return <GameLayout token={token} onLogout={onLogout}><div className="dropin-loading">⏳ Loading…</div></GameLayout>;
  if (error)   return <GameLayout token={token} onLogout={onLogout}><div className="dropin-empty"><div className="dropin-empty__icon">⚠️</div><div>{error}</div></div></GameLayout>;
  if (!dropIn) return null;

  const d    = dropIn;
  const isHost = userId && (d.hostId === userId || d.host_id === userId);
  const skillLevel = d.skillLevel || d.skill_level;
  const status     = d.status;
  const players    = d.players || [];
  const confirmed  = players.filter((p) => p.status === 'confirmed');
  const waitlisted = players.filter((p) => p.status === 'waitlisted');
  const checkedIn  = players.filter((p) => p.status === 'checked_in');
  const maxPlayers   = d.maxPlayers || d.max_players;
  const maxWaitlist      = d.maxWaitlist ?? d.max_waitlist ?? 5;
  const activePlayers    = confirmed.length + checkedIn.length;
  const maxPlayersPerTeam = d.playersPerTeam ?? d.players_per_team ?? 6;
  // Max valid teams: largest N where last team still gets ≥1 player
  // = ceil(activePlayers / ppt), capped at 8
  const maxValidTeams = activePlayers >= 2
    ? Math.min(8, Math.ceil(activePlayers / maxPlayersPerTeam))
    : 2;

  const myRecord = userId ? players.find((p) => p.user_id === userId) : null;
  const myStatus = myRecord?.status;
  const priceCents = d.priceCents ?? d.price_cents ?? 0;
  const price = priceCents > 0 ? formatMoney(priceCents, d.currency) : null;
  const iOwe = Boolean(price) && myRecord?.payment_status === 'pending'
    && (myStatus === 'confirmed' || myStatus === 'checked_in');

  const notify = (msg) => {
    setActionMsg(msg);
    setTimeout(() => setActionMsg(null), 4000);
  };

  const doAction = async (fn) => {
    setActionLoading(true);
    try {
      await fn();
      await load();
    } catch (err) {
      notify('⚠️ ' + (err.message || 'Action failed'));
    } finally {
      setActionLoading(false);
    }
  };

  // A paid seat is held while the player pays on Stripe's page
  const goToCheckout = (res) => {
    const url = res?.data?.checkoutUrl;
    if (res?.data?.status === 'payment_required' && url) {
      window.location.assign(url);
      return true;
    }
    return false;
  };
  const handleJoin  = () => doAction(async () => { goToCheckout(await dropInService.join(id)); });
  const handlePay   = () => doAction(async () => { goToCheckout(await dropInService.pay(id)); });
  // Leaving asks first and says what happens to a payment (DI: no surprise refunds)
  const handleLeave = () => setLeaveOpen(true);
  const confirmLeave = () => {
    setLeaveOpen(false);
    doAction(async () => {
      const res = await dropInService.leave(id);
      const msg = res?.data?.message || res?.message;
      if (msg) notify(msg);
    });
  };
  const handleAcceptInvite = () => doAction(() => dropInService.acceptInvite(id));
  const handleRejectInvite = () => doAction(() => dropInService.rejectInvite(id));

  const handleRemovePlayer = (playerId, playerName) =>
    setRemoveConfirm({ isOpen: true, playerId, playerName });

  const confirmRemovePlayer = () => {
    const { playerId } = removeConfirm;
    setRemoveConfirm({ isOpen: false, playerId: null, playerName: '' });
    doAction(() => dropInService.removePlayer(id, playerId));
  };

  const handleInviteSearch = async (q) => {
    setInviteSearch(q);
    if (q.length < 2) { setInviteResults([]); return; }
    setInviteSearching(true);
    try {
      const res = await dropInService.searchUsersForInvite(id, q);
      setInviteResults(res.data?.users || []);
    } catch { setInviteResults([]); }
    finally { setInviteSearching(false); }
  };

  const handleInvitePlayer = (targetUserId) =>
    doAction(async () => {
      await dropInService.invitePlayer(id, targetUserId);
      setInviteSearch('');
      setInviteResults([]);
    });

  // Move a player from their team into the bench
  const moveFromTeamToBench = (userId, fromSlot) => {
    const fromTeam = inlineTeams?.find((t) => t.slot === fromSlot);
    const player   = fromTeam?.players.find((p) => p.user_id === userId);
    if (!player) return;
    setInlineBench((prev) => [...prev, player]);
    setInlineTeams((prev) =>
      prev.map((t) =>
        t.slot !== fromSlot
          ? t
          : { ...t, players: t.players.filter((p) => p.user_id !== userId) }
      )
    );
  };

  // Move a bench player into a team
  const moveFromBenchToTeam = (userId, toSlot) => {
    const player = inlineBench.find((p) => p.user_id === userId);
    if (!player) return;
    setInlineBench((prev) => prev.filter((p) => p.user_id !== userId));
    setInlineTeams((prev) =>
      prev.map((t) =>
        t.slot !== toSlot ? t : { ...t, players: [...t.players, player] }
      )
    );
  };

  const handleGenerateTeams = (n = numTeams) => doAction(async () => {
    const res   = await dropInService.generateTeams(id, n);
    const raw   = res.data?.teams || [];
    // Balanced teams; players beyond the team size come back on the bench
    const teams = raw.map((team) => ({ ...team, players: [...team.players] }));
    const bench = [...(res.data?.bench || [])];
    setInlineTeams(teams);
    setInlineBench(bench);
    setInlineNames(Object.fromEntries(teams.map((team) => [team.slot, team.name])));
    setConfirmedTeams(null);
    setActiveTab('teams');
  });

  const handleConfirmTeams = () => {
    if (!inlineTeams) return;
    const final  = inlineTeams.map((t) => ({ ...t, name: inlineNames[t.slot] ?? t.name }));
    const ppt    = maxPlayersPerTeam;
    // Teams may be short of the full size, but not over it and not lopsided
    const sizes  = final.map((t) => t.players.length);
    const over   = final.filter((t) => t.players.length > ppt);
    if (over.length > 0) {
      const detail = over.map((t) => `${inlineNames[t.slot] ?? t.name} (${t.players.length}/${ppt})`).join(', ');
      notify(`⚠️ Teams can have at most ${ppt} players each. Fix: ${detail}`);
      return;
    }
    if (sizes.some((n) => n === 0) || Math.max(...sizes) - Math.min(...sizes) > 1) {
      notify('⚠️ Every team needs players, and team sizes may differ by at most one.');
      return;
    }
    setConfirmedTeams(final);
    const setups = [];
    for (let i = 0; i < final.length; i++) {
      for (let j = i + 1; j < final.length; j++) {
        setups.push({ teamASlot: final[i].slot, teamBSlot: final[j].slot, selected: true });
      }
    }
    setMatchSetups(setups);
    setActiveTab('matches');
  };

  const handleCreateAllSelected = async () => {
    const toCreate = matchSetups.filter((m) => !m.created && m.selected);
    if (!toCreate.length) return;
    setActionLoading(true);
    const createdKeys = new Set();
    try {
      for (const setup of toCreate) {
        const tA = confirmedTeams.find((t) => t.slot === setup.teamASlot);
        const tB = confirmedTeams.find((t) => t.slot === setup.teamBSlot);
        await dropInService.createMatch(id, {
          team_a_name:    tA.name,
          team_b_name:    tB.name,
          team_a_players: tA.players,
          team_b_players: tB.players,
        });
        createdKeys.add(`${setup.teamASlot}-${setup.teamBSlot}`);
      }
      setMatchSetups((prev) =>
        prev.map((m) =>
          createdKeys.has(`${m.teamASlot}-${m.teamBSlot}`) ? { ...m, created: true } : m
        )
      );
      const res = await dropInService.getMatches(id);
      setDropInMatches(res.data?.matches || []);
      setActiveTab('matches');
      notify(`✅ ${createdKeys.size} match${createdKeys.size !== 1 ? 'es' : ''} created!`);
    } catch (err) {
      notify('⚠️ ' + (err.message || 'Failed to create matches'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleCheckIn = (playerId) =>
    doAction(() => dropInService.checkIn(id, playerId));

  // ── Messages ───────────────────────────────────────────────────────────────
  const handleSendMessage = async (messageType = 'message') => {
    if (!messageInput.trim()) return;
    setMessageSending(true);
    try {
      const res = await dropInService.sendMessage(id, messageInput.trim(), messageType);
      const newMsg = res.data?.message || res.data || res;
      setMessages((prev) => [...prev, newMsg]);
      setMessageInput('');
    } catch (err) {
      notify('⚠️ ' + (err.message || 'Failed to send message'));
    } finally {
      setMessageSending(false);
    }
  };

  // ── Permanent members ──────────────────────────────────────────────────────
  const handleMemberSearch = async (q) => {
    setMemberSearch(q);
    if (q.length < 2) { setMemberResults([]); return; }
    setMemberSearching(true);
    try {
      const res = await dropInService.searchUsersForInvite(id, q);
      // Filter out users already in members list
      const existing = new Set(members.map((m) => m.user_id));
      setMemberResults((res.data?.users || []).filter((u) => !existing.has(u.id)));
    } catch { setMemberResults([]); }
    finally { setMemberSearching(false); }
  };

  const handleAddMember = (targetUserId) =>
    doAction(async () => {
      const res = await dropInService.addMember(id, targetUserId);
      const added = res.data?.member || res.data || res;
      setMembers((prev) => [...prev.filter((m) => m.user_id !== targetUserId), added]);
      setMemberSearch('');
      setMemberResults([]);
    });

  const handleRemoveMember = (targetUserId) =>
    doAction(async () => {
      await dropInService.removeMember(id, targetUserId);
      setMembers((prev) => prev.filter((m) => m.user_id !== targetUserId));
    });

  // ── Recurring: open this week's session ────────────────────────────────────
  const handleOpenWeekly = async () => {
    setWeeklyOpening(true);
    try {
      const res = await dropInService.openWeeklySession(id);
      const newSession = res.data?.dropIn || res.data?.session || res.data;
      notify('✅ New weekly session opened!');
      if (newSession?.id) setTimeout(() => navigate(`/drop-in/${eid(newSession)}`), 1500);
    } catch (err) {
      notify('⚠️ ' + (err.message || 'Failed to open weekly session'));
    } finally {
      setWeeklyOpening(false);
    }
  };

  const SKILL_STYLE = {
    recreational: { background: 'rgba(74,222,128,0.15)', color: '#4ade80' },
    intermediate:  { background: 'rgba(251,191,36,0.15)',  color: '#fbbf24' },
    competitive:   { background: 'rgba(239,68,68,0.15)',   color: '#f87171' },
  };
  const STATUS_BADGE = {
    open:        { bg: 'rgba(74,222,128,0.15)',  color: '#4ade80',  label: 'Open' },
    in_progress: { bg: 'rgba(251,191,36,0.15)',  color: '#fbbf24',  label: 'In Progress' },
    completed:   { bg: 'rgba(148,163,184,0.15)', color: '#94a3b8',  label: 'Completed' },
    cancelled:   { bg: 'rgba(239,68,68,0.15)',   color: '#f87171',  label: 'Cancelled' },
  };
  const VISIBILITY_BADGE = {
    public:  { bg: 'rgba(56,189,248,0.15)',  color: '#38bdf8', label: '🌐 Public' },
    private: { bg: 'rgba(167,139,250,0.15)', color: '#a78bfa', label: '🔒 Private' },
  };
  const sb = STATUS_BADGE[status] || STATUS_BADGE.open;
  const vb = VISIBILITY_BADGE[(d.visibility || 'public')] || VISIBILITY_BADGE.public;

  return (
    <GameLayout token={token} onLogout={onLogout}>
      <div className="dropin-page">

        {actionMsg && (
          <div style={{
            background: 'rgba(167,139,250,0.1)',
            border: '1px solid rgba(167,139,250,0.3)',
            borderRadius: 10, padding: '0.75rem 1rem',
            color: '#e2e8f0', fontSize: '0.875rem', marginBottom: '1rem',
          }}>
            {actionMsg}
          </div>
        )}

        {paymentNote && (
          <div role="status" data-testid="dropin-payment-note" style={{
            background: 'rgba(74,222,128,0.1)',
            border: '1px solid rgba(74,222,128,0.3)',
            borderRadius: 10, padding: '0.75rem 1rem',
            color: '#e2e8f0', fontSize: '0.875rem', marginBottom: '1rem',
          }}>
            {paymentNote}
          </div>
        )}

        {/* Hero */}
        <div className="dropin-detail-hero">
          {/* Top row: badges + back button */}
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '0.5rem' }}>
            <div className="dropin-detail-hero__sport" style={{ marginBottom: 0 }}>
              🏅 {d.sportName || d.sport_name}
            <span style={{
              marginLeft: '0.75rem', padding: '0.15rem 0.5rem', borderRadius: 99,
              fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
              background: sb.bg, color: sb.color,
            }}>
              {sb.label}
            </span>
            <span style={{
              marginLeft: '0.5rem', padding: '0.15rem 0.5rem', borderRadius: 99,
              fontSize: '0.7rem', fontWeight: 700,
              ...(SKILL_STYLE[skillLevel] || {}),
            }}>
              {skillLevel}
            </span>
            <span style={{
              marginLeft: '0.5rem', padding: '0.15rem 0.5rem', borderRadius: 99,
              fontSize: '0.7rem', fontWeight: 700,
              background: vb.bg, color: vb.color,
            }}>
              {vb.label}
            </span>
            {(d.isRecurring || d.is_recurring) && (
              <span style={{
                marginLeft: '0.5rem', padding: '0.15rem 0.5rem', borderRadius: 99,
                fontSize: '0.7rem', fontWeight: 700,
                background: 'rgba(56,189,248,0.15)', color: '#38bdf8',
              }}>
                🔄 Recurring
              </span>
            )}
            </div>{/* end dropin-detail-hero__sport */}
            <button
              onClick={() => navigate('/drop-in')}
              className="dropin-btn dropin-btn--secondary"
              style={{ width: 'auto', padding: '0.3rem 0.85rem', fontSize: '0.78rem', flexShrink: 0, marginTop: '0.1rem' }}
            >
              ← Back
            </button>
          </div>{/* end top-row flex */}
          <h1 className="dropin-detail-hero__title">{d.title}</h1>
          {d.description && (
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.9rem', marginBottom: '1rem', textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}>{d.description}</p>
          )}
          <div className="dropin-detail-hero__meta">
            <span>📅 {fmtDate(d.scheduledAt || d.scheduled_at, eventTimeZone(d))}</span>
            {(d.venue || d.venueAddress) && (
              <span>📍 {d.venue || d.venueAddress}</span>
            )}
            {d.court && <span>🏟️ {d.court}</span>}
            <span data-testid="dropin-price">💵 {price ? `${price} per player` : 'Free'}</span>
            <span
              style={{ cursor: 'pointer', color: '#b8acff', textDecoration: 'underline' }}
              onClick={() => { const uid = d.hostUuid || d.host_uuid || d.hostId || d.host_id; if (uid) navigate(`/players/${uid}`); }}
            >
              Host: {d.hostName || d.host_name}
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="dropin-detail-body">

          {/* Left: tabbed content */}
          <div>
            {/* Tab navigation — all users see players/teams/matches/standings; host-only actions inside */}
            {(status === 'open' || status === 'in_progress' || status === 'completed') && (
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <button
                  className={`dropin-btn ${activeTab === 'players' ? 'dropin-btn--primary' : 'dropin-btn--secondary'}`}
                  style={{ width: 'auto', padding: '0.4rem 0.9rem', fontSize: '0.8rem' }}
                  onClick={() => setActiveTab('players')}
                >
                  👥 Players
                </button>
                <button
                  className={`dropin-btn ${activeTab === 'teams' ? 'dropin-btn--primary' : 'dropin-btn--secondary'}`}
                  style={{ width: 'auto', padding: '0.4rem 0.9rem', fontSize: '0.8rem' }}
                  onClick={() => setActiveTab('teams')}
                >
                  ⚽ Teams{inlineTeams ? ` (${inlineTeams.length})` : ''}
                </button>
                <button
                  className={`dropin-btn ${activeTab === 'matches' ? 'dropin-btn--primary' : 'dropin-btn--secondary'}`}
                  style={{ width: 'auto', padding: '0.4rem 0.9rem', fontSize: '0.8rem' }}
                  onClick={() => setActiveTab('matches')}
                >
                  🎮 Matches{dropInMatches.length > 0 ? ` (${dropInMatches.length})` : ''}
                </button>
                <button
                  className={`dropin-btn ${activeTab === 'standings' ? 'dropin-btn--primary' : 'dropin-btn--secondary'}`}
                  style={{ width: 'auto', padding: '0.4rem 0.9rem', fontSize: '0.8rem' }}
                  onClick={() => { setActiveTab('standings'); }}
                >
                  🏆 Standings
                </button>
                <button
                  className={`dropin-btn ${activeTab === 'messages' ? 'dropin-btn--primary' : 'dropin-btn--secondary'}`}
                  style={{ width: 'auto', padding: '0.4rem 0.9rem', fontSize: '0.8rem' }}
                  onClick={() => setActiveTab('messages')}
                >
                  💬 Messages{messages.length > 0 ? ` (${messages.length})` : ''}
                </button>
              </div>
            )}

            {/* ── Players tab ────────────────────────────────────── */}
            {activeTab === 'players' && (
              <div>
                {/* Capacity */}
                <div className="dropin-capacity-info">
                  <span>
                    <strong>{activePlayers}</strong> / {maxPlayers} players confirmed
                  </span>
                  {waitlisted.length > 0 && (
                    <span style={{ color: '#fbbf24' }}>
                      ⏳ {waitlisted.length}{maxWaitlist > 0 ? `/${maxWaitlist}` : ''} on waitlist
                    </span>
                  )}
                  {activePlayers >= maxPlayers && waitlisted.length === 0 && maxWaitlist > 0 && (
                    <span style={{ color: '#f87171' }}>• Waitlist open ({maxWaitlist} spots)</span>
                  )}
                  {activePlayers >= maxPlayers && maxWaitlist === 0 && (
                    <span style={{ color: '#f87171' }}>• Full — no waitlist</span>
                  )}
                </div>

                {/* Checked-in players */}
                {checkedIn.length > 0 && (
                  <div className="dropin-players-section" style={{ marginBottom: '1rem' }}>
                    <h4 className="dropin-section-title">✅ Checked In ({checkedIn.length})</h4>
                    {checkedIn.map((p) => (
                      <div key={p.user_id} className="dropin-player-row">
                        <div className="dropin-player-avatar">{initials(p.display_name)}</div>
                        <div>
                          <div className="dropin-player-name"
                            style={{ cursor: 'pointer', color: '#b8acff' }}
                            onClick={() => navigate(`/players/${p.uuid || p.user_id}`)}
                          >{p.display_name}</div>
                          {p.team_slot && (
                            <div style={{ fontSize: '0.7rem', color: TEAM_COLORS[(p.team_slot - 1) % TEAM_COLORS.length] }}>
                              Team {p.team_slot}
                            </div>
                          )}
                        </div>
                        <span className="dropin-player-badge dropin-player-badge--checked_in">On Court</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Confirmed players */}
                <div className="dropin-players-section" style={{ marginBottom: '1rem' }}>
                  <h4 className="dropin-section-title">🙋 Confirmed ({confirmed.length})</h4>
                  {confirmed.length === 0 ? (
                    <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.875rem' }}>No confirmed players yet.</p>
                  ) : confirmed.map((p) => (
                    <div key={p.user_id} className="dropin-player-row">
                      <div className="dropin-player-avatar">{initials(p.display_name)}</div>
                      <div>
                        <div className="dropin-player-name"
                          style={{ cursor: 'pointer', color: '#b8acff' }}
                          onClick={() => navigate(`/players/${p.uuid || p.user_id}`)}
                        >{p.display_name}</div>
                        {p.team_slot && (
                          <div style={{ fontSize: '0.7rem', color: TEAM_COLORS[(p.team_slot - 1) % TEAM_COLORS.length] }}>
                            Team {p.team_slot}
                          </div>
                        )}
                      </div>
                      <span className="dropin-player-badge dropin-player-badge--confirmed">Confirmed</span>
                      {isHost && price && p.user_id !== userId && (
                        <span className={`dropin-player-badge ${p.payment_status === 'paid' ? 'dropin-player-badge--paid' : 'dropin-player-badge--unpaid'}`}>
                          {p.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                        </span>
                      )}
                      {isHost && status === 'in_progress' && (
                        <button
                          className="dropin-btn dropin-btn--secondary"
                          style={{ width: 'auto', padding: '0.25rem 0.6rem', fontSize: '0.7rem', marginLeft: '0.25rem' }}
                          onClick={() => handleCheckIn(p.user_id)}
                          disabled={actionLoading}
                        >
                          Check In
                        </button>
                      )}
                      {isHost && p.user_id !== userId && (
                        <button
                          className="dropin-btn dropin-btn--danger"
                          style={{ width: 'auto', padding: '0.25rem 0.6rem', fontSize: '0.7rem', marginLeft: '0.25rem' }}
                          onClick={() => handleRemovePlayer(p.user_id, p.display_name)}
                          disabled={actionLoading}
                          title="Remove player"
                          aria-label={`Remove ${p.display_name || "player"}`}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Waitlist */}
                {waitlisted.length > 0 && (
                  <div className="dropin-players-section">
                    <h4 className="dropin-section-title">⏳ Waitlist ({waitlisted.length})</h4>
                    {waitlisted.map((p, wIdx) => (
                      <div key={p.user_id} className="dropin-player-row">
                        <div className="dropin-player-avatar" style={{ background: '#374151', fontSize: '0.75rem' }}>
                          #{wIdx + 1}
                        </div>
                        <div className="dropin-player-name"
                          style={{ cursor: 'pointer', color: '#b8acff' }}
                          onClick={() => navigate(`/players/${p.uuid || p.user_id}`)}
                        >{p.display_name}</div>
                        <span className="dropin-player-badge dropin-player-badge--waitlisted">Waitlist</span>
                        {isHost && (
                          <button
                            className="dropin-btn dropin-btn--danger"
                            style={{ width: 'auto', padding: '0.25rem 0.6rem', fontSize: '0.7rem', marginLeft: '0.25rem' }}
                            onClick={() => handleRemovePlayer(p.user_id, p.display_name)}
                            disabled={actionLoading}
                            title="Remove from waitlist"
                            aria-label="Remove from waitlist"
                          >
                            ✕
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Private drop-in: Permanent members (host only) */}
                {isHost && d.visibility === 'private' && (
                  <div style={{ marginTop: '1.5rem' }}>
                    <h4 className="dropin-section-title">🔒 Permanent Members</h4>
                    <p style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: '0.75rem' }}>
                      Permanently invited to this private drop-in. On recurring sessions they get first-come-first-serve access.
                    </p>
                    {membersLoading ? (
                      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>Loading…</p>
                    ) : members.length === 0 ? (
                      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>No permanent members yet. Search below to add.</p>
                    ) : members.map((m) => (
                      <div key={m.user_id} className="dropin-player-row">
                        <div className="dropin-player-avatar">{initials(m.user_name || '')}</div>
                        <div
                          className="dropin-player-name"
                          style={{ cursor: 'pointer', color: '#b8acff', flex: 1 }}
                          onClick={() => navigate(`/players/${m.uuid || m.user_id}`)}
                        >
                          {m.user_name}
                        </div>
                        <button
                          className="dropin-btn dropin-btn--danger"
                          style={{ width: 'auto', padding: '0.25rem 0.6rem', fontSize: '0.7rem' }}
                          onClick={() => handleRemoveMember(m.user_id)}
                          disabled={actionLoading}
                          title="Remove from members list"
                          aria-label="Remove from members list"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    {/* Add member search */}
                    <div style={{ marginTop: '0.75rem' }}>
                      <input
                        type="text"
                        value={memberSearch}
                        onChange={(e) => handleMemberSearch(e.target.value)}
                        placeholder="Search to add a permanent member…"
                        className="dropin-filter-select"
                        style={{ borderRadius: 8, width: '100%' }}
                      />
                      {memberSearching && (
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.25rem' }}>Searching…</p>
                      )}
                      {memberResults.length > 0 && (
                        <div style={{
                          marginTop: '0.4rem',
                          background: 'rgba(15,23,42,0.95)',
                          border: '1px solid rgba(255,255,255,0.15)',
                          borderRadius: 10, overflow: 'hidden',
                        }}>
                          {memberResults.map((u) => (
                            <div key={u.id} style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                              padding: '0.5rem 0.75rem',
                              borderBottom: '1px solid rgba(255,255,255,0.07)',
                              fontSize: '0.8rem',
                            }}>
                              <span style={{ color: 'rgba(255,255,255,0.85)' }}>
                                {u.display_name}
                                {u.username && <span style={{ color: 'rgba(255,255,255,0.45)', marginLeft: '0.4rem' }}>@{u.username}</span>}
                              </span>
                              <button
                                className="dropin-btn dropin-btn--primary"
                                style={{ width: 'auto', padding: '0.2rem 0.7rem', fontSize: '0.75rem' }}
                                onClick={() => handleAddMember(u.id)}
                                disabled={actionLoading}
                              >
                                + Add
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {memberSearch.length >= 2 && !memberSearching && memberResults.length === 0 && (
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.25rem' }}>No users found</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ── Teams tab ──────────────────────────────────────── */}
            {activeTab === 'teams' && (
              <div>
                {/* Host-only controls: only during in_progress */}
                {isHost && status === 'in_progress' && (
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
                  <select
                    value={numTeams}
                    onChange={(e) => setNumTeams(parseInt(e.target.value))}
                    className="dropin-filter-select"
                    style={{ flex: 1 }}
                  >
                    {Array.from({ length: Math.max(0, maxValidTeams - 1) }, (_, i) => i + 2).map((n) => (
                      <option key={n} value={n}>{n} Teams</option>
                    ))}
                  </select>
                  <button
                    className="dropin-btn dropin-btn--primary"
                    style={{ flex: 1 }}
                    onClick={() => handleGenerateTeams(numTeams)}
                    disabled={actionLoading || activePlayers < 2}
                  >
                    🎲 {inlineTeams ? 'Re-shuffle' : 'Generate Teams'}
                  </button>
                </div>
                )} {/* end host-only generate controls */}

                {!inlineTeams ? (
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', textAlign: 'center', padding: '2rem 0' }}>
                    {isHost && status === 'in_progress'
                      ? 'Click "Generate Teams" to assign players to teams.'
                      : 'Teams have not been generated yet.'}
                  </p>
                ) : (
                  <>
                    {isHost && (
                      <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.8rem', marginBottom: '1rem' }}>
                        Drag players between teams to swap · Click ✕ to bench a player · Click a team name to rename
                      </p>
                    )}
                    <div className="dropin-teams-grid">
                      {inlineTeams.map((team, idx) => {
                        const ppt      = maxPlayersPerTeam;
                        const count    = team.players.length;
                        const balanced = count === ppt;
                        const color    = TEAM_COLORS[idx % TEAM_COLORS.length];
                        return (
                        <div
                          key={team.slot}
                          className={`dropin-team-col${inlineDragOver === team.slot ? ' dropin-team-col--dropzone' : ''}`}
                          onDragOver={(e) => { e.preventDefault(); setInlineDragOver(team.slot); }}
                          onDragLeave={() => setInlineDragOver(null)}
                          onDrop={(e) => {
                            e.preventDefault();
                            setInlineDragOver(null);
                            if (!inlineDragging || inlineDragging.fromSlot === team.slot) { setInlineDragging(null); return; }
                            if (inlineDragging.fromSlot === 'bench') {
                              moveFromBenchToTeam(inlineDragging.userId, team.slot);
                            } else {
                              setInlineTeams((prev) => {
                                const next = prev.map((t) => ({ ...t, players: [...t.players] }));
                                const from = next.find((t) => t.slot === inlineDragging.fromSlot);
                                const to   = next.find((t) => t.slot === team.slot);
                                const pi   = from.players.findIndex((p) => p.user_id === inlineDragging.userId);
                                if (pi === -1) return prev;
                                const [player] = from.players.splice(pi, 1);
                                to.players.push(player);
                                return next;
                              });
                            }
                            setInlineDragging(null);
                          }}
                        >
                          {inlineEditSlot === team.slot ? (
                            <input
                              autoFocus
                              className="dropin-team-name-input"
                              style={{ color }}
                              value={inlineNames[team.slot] ?? team.name}
                              onChange={(e) => setInlineNames((n) => ({ ...n, [team.slot]: e.target.value }))}
                              onBlur={() => setInlineEditSlot(null)}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') setInlineEditSlot(null); }}
                            />
                          ) : (
                            <div
                              className="dropin-team-col__name dropin-team-col__name--editable"
                              style={{ color }}
                              onClick={() => setInlineEditSlot(team.slot)}
                              title="Click to rename"
                            >
                              {inlineNames[team.slot] ?? team.name} ✏️
                            </div>
                          )}
                          {/* Player count with balance indicator */}
                          <div style={{ fontSize: '0.7rem', textAlign: 'center', marginBottom: '0.5rem', color: balanced ? '#4ade80' : '#fbbf24' }}>
                            {count} / {ppt} player{ppt !== 1 ? 's' : ''} {balanced ? '✓' : '⚠️'}
                          </div>
                          {team.players.length === 0 && (
                            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)', textAlign: 'center', padding: '0.5rem 0' }}>
                              Drop a player here
                            </div>
                          )}
                          {team.players.map((p) => {
                            const name = p.display_name || `${p.first_name} ${p.last_name}`;
                            return (
                              <div
                                key={p.user_id}
                                className={`dropin-team-player dropin-team-player--draggable${inlineDragging?.userId === p.user_id ? ' dragging' : ''}`}
                                draggable
                                onDragStart={(e) => { setInlineDragging({ userId: p.user_id, fromSlot: team.slot }); e.dataTransfer.effectAllowed = 'move'; }}
                                onDragEnd={() => setInlineDragging(null)}
                              >
                                <div
                                  className="dropin-team-player__avatar"
                                  style={{ background: `linear-gradient(135deg, ${color}, #0f172a)` }}
                                >
                                  {initials(name)}
                                </div>
                                <span style={{ flex: 1 }}>{name}</span>
                                {isHost && (
                                  <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); moveFromTeamToBench(p.user_id, team.slot); }}
                                    title="Move to bench"
                                    aria-label="Move to bench"
                                    style={{
                                      background: 'none', border: 'none', cursor: 'pointer',
                                      color: 'rgba(255,255,255,0.35)', fontSize: '0.7rem', padding: '0 0.1rem',
                                      lineHeight: 1, flexShrink: 0,
                                    }}
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                        );
                      })}
                    </div>
                    {isHost && (<>
                    {/* Bench — overflow players; drag from here to a team or drag a team player here */}
                    <div
                      style={{
                        marginTop: '1rem',
                        border: `1px dashed ${inlineDragOver === 'bench' ? '#a78bfa' : 'rgba(255,255,255,0.12)'}`,
                        borderRadius: 10,
                        padding: '0.65rem 0.75rem',
                        background: inlineDragOver === 'bench' ? 'rgba(167,139,250,0.08)' : 'rgba(255,255,255,0.025)',
                        transition: 'border-color 0.15s, background 0.15s',
                      }}
                      onDragOver={(e) => { e.preventDefault(); setInlineDragOver('bench'); }}
                      onDragLeave={() => setInlineDragOver(null)}
                      onDrop={(e) => {
                        e.preventDefault();
                        setInlineDragOver(null);
                        if (!inlineDragging || inlineDragging.fromSlot === 'bench') { setInlineDragging(null); return; }
                        moveFromTeamToBench(inlineDragging.userId, inlineDragging.fromSlot);
                        setInlineDragging(null);
                      }}
                    >
                      <div style={{ fontSize: '0.75rem', color: '#94a3b8', marginBottom: inlineBench.length ? '0.5rem' : 0, fontWeight: 600 }}>
                        🪑 Bench ({inlineBench.length}) — drag a player here to remove from team · drag bench players to a team
                      </div>
                      {inlineBench.length === 0 ? (
                        <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.2)', fontStyle: 'italic' }}>
                          No players on bench
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
                          {inlineBench.map((p) => {
                            const name = p.display_name || `${p.first_name} ${p.last_name}`;
                            return (
                              <div
                                key={p.user_id}
                                className={`dropin-team-player dropin-team-player--draggable${inlineDragging?.userId === p.user_id ? ' dragging' : ''}`}
                                draggable
                                onDragStart={(e) => { setInlineDragging({ userId: p.user_id, fromSlot: 'bench' }); e.dataTransfer.effectAllowed = 'move'; }}
                                onDragEnd={() => setInlineDragging(null)}
                                style={{ minWidth: 0, maxWidth: '140px' }}
                              >
                                <div className="dropin-team-player__avatar" style={{ background: 'linear-gradient(135deg,#475569,#0f172a)', flexShrink: 0 }}>
                                  {initials(name)}
                                </div>
                                <span style={{ flex: 1, fontSize: '0.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <button
                      className="dropin-btn dropin-btn--success"
                      style={{ marginTop: '1rem' }}
                      onClick={handleConfirmTeams}
                      disabled={actionLoading}
                    >
                      ✅ Confirm Teams — Plan Matches →
                    </button>
                    </>)}
                  </>
                )}
              </div>
            )}

            {/* ── Matches tab ────────────────────────────────────── */}
            {activeTab === 'matches' && (
              <div>
                {/* Plan matchups section — host only while teams are confirmed and some not yet created */}
                {isHost && confirmedTeams && matchSetups.some((m) => !m.created) && (
                  <div style={{ marginBottom: '1.5rem' }}>
                    <h4 className="dropin-section-title" style={{ marginBottom: '0.75rem' }}>🎮 Plan Matches</h4>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'center' }}>
                      {confirmedTeams.map((t, idx) => (
                        <div key={t.slot} style={{
                          background: 'rgba(255,255,255,0.05)',
                          border: `1px solid ${TEAM_COLORS[idx % TEAM_COLORS.length]}55`,
                          borderRadius: 8, padding: '0.35rem 0.75rem', fontSize: '0.8rem',
                        }}>
                          <span style={{ color: TEAM_COLORS[idx % TEAM_COLORS.length], fontWeight: 700 }}>{t.name}</span>
                          <span style={{ color: 'rgba(255,255,255,0.4)', marginLeft: '0.4rem' }}>{t.players.length}p</span>
                        </div>
                      ))}
                      <button
                        className="dropin-btn dropin-btn--secondary"
                        style={{ width: 'auto', padding: '0.3rem 0.7rem', fontSize: '0.75rem' }}
                        onClick={() => setActiveTab('teams')}
                      >
                        ✏️ Edit Teams
                      </button>
                    </div>
                    <p style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '0.75rem' }}>
                      Check the matchups you want to play, then hit the bulk create button below:
                    </p>
                    {matchSetups.filter((m) => !m.created).map((setup, mIdx) => {
                      const tA   = confirmedTeams.find((t) => t.slot === setup.teamASlot);
                      const tB   = confirmedTeams.find((t) => t.slot === setup.teamBSlot);
                      const aIdx = confirmedTeams.indexOf(tA);
                      const bIdx = confirmedTeams.indexOf(tB);
                      if (!tA || !tB) return null;
                      return (
                        <div key={mIdx} style={{
                          display: 'flex', alignItems: 'center', gap: '0.75rem',
                          background: 'rgba(255,255,255,0.04)',
                          border: `1px solid ${setup.selected ? 'rgba(167,139,250,0.35)' : 'rgba(255,255,255,0.07)'}`,
                          borderRadius: 10, padding: '0.6rem 0.9rem', marginBottom: '0.5rem',
                        }}>
                          <input
                            type="checkbox"
                            checked={setup.selected}
                            onChange={() => setMatchSetups((prev) =>
                              prev.map((m, i) => i === mIdx ? { ...m, selected: !m.selected } : m)
                            )}
                            style={{ accentColor: '#a78bfa', width: 16, height: 16, flexShrink: 0 }}
                          />
                          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.875rem' }}>
                            <span style={{ color: TEAM_COLORS[aIdx % TEAM_COLORS.length], fontWeight: 700 }}>{tA.name}</span>
                            <span style={{ color: '#64748b' }}>vs</span>
                            <span style={{ color: TEAM_COLORS[bIdx % TEAM_COLORS.length], fontWeight: 700 }}>{tB.name}</span>
                          </div>

                        </div>
                      );
                    })}
                    {/* Bulk create button */}
                    {(() => {
                      const n = matchSetups.filter((m) => !m.created && m.selected).length;
                      return n > 0 ? (
                        <button
                          className="dropin-btn dropin-btn--success"
                          style={{ marginTop: '0.75rem' }}
                          onClick={handleCreateAllSelected}
                          disabled={actionLoading}
                        >
                          🎮 Create {n} Match{n !== 1 ? 'es' : ''}
                        </button>
                      ) : (
                        <p style={{ fontSize: '0.78rem', color: 'rgba(255,255,255,0.3)', marginTop: '0.5rem', textAlign: 'center' }}>
                          Check at least one matchup above to enable creation.
                        </p>
                      );
                    })()}
                  </div>
                )}

                {/* Created matches list */}
                <h4 className="dropin-section-title" style={{ marginBottom: '0.75rem' }}>
                  🎮 Matches ({dropInMatches.length})
                </h4>
                {matchesLoading ? (
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>Loading…</p>
                ) : dropInMatches.length === 0 ? (
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', textAlign: 'center', padding: '1.5rem 0' }}>
                    No matches created yet.{confirmedTeams ? ' Plan and create matchups above.' : ' Generate and confirm teams first.'}
                  </p>
                ) : dropInMatches.map((m) => {
                  const STATUS_COLOR = {
                    scheduled:   '#94a3b8',
                    in_progress: '#fbbf24',
                    completed:   '#4ade80',
                    cancelled:   '#f87171',
                  };
                  const sColor = STATUS_COLOR[m.status] || '#94a3b8';
                  return (
                    <div
                      key={m.id}
                      onClick={() => navigate(`/matches/${m.short_code || m.uuid || m.id}`)}
                      style={{
                        cursor: 'pointer',
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 10, padding: '0.75rem 1rem', marginBottom: '0.5rem',
                        display: 'flex', alignItems: 'center', gap: '0.75rem',
                        transition: 'border-color 0.15s',
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(167,139,250,0.45)'}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'}
                    >
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700, color: '#e2e8f0', fontSize: '0.9rem', marginBottom: '0.2rem' }}>
                          {matchLine(m)}
                        </div>
                        <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)' }}>
                          {new Date(m.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                      <span style={{
                        padding: '0.2rem 0.6rem', borderRadius: 99,
                        fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
                        background: `${sColor}22`, color: sColor,
                      }}>
                        {m.status}
                      </span>
                      <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem' }}>›</span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Standings tab ──────────────────────────────────── */}
            {activeTab === 'standings' && (() => {
              const standings = {};
              dropInMatches.forEach((m) => {
                const config = m.match_config?.drop_in_teams;
                if (!config) return;
                const teamA = config.team_a?.name || 'Team A';
                const teamB = config.team_b?.name || 'Team B';
                if (!standings[teamA]) standings[teamA] = { name: teamA, played: 0, won: 0, lost: 0, drawn: 0, points: 0 };
                if (!standings[teamB]) standings[teamB] = { name: teamB, played: 0, won: 0, lost: 0, drawn: 0, points: 0 };
                if (m.status !== 'completed') return;
                standings[teamA].played++;
                standings[teamB].played++;
                if (m.result_type === 'draw') {
                  standings[teamA].drawn++; standings[teamA].points++;
                  standings[teamB].drawn++; standings[teamB].points++;
                } else if (m.winner_id) {
                  const winnerIsA = m.participant_1_id === m.winner_id;
                  const winnerName = winnerIsA ? config.team_a?.name : config.team_b?.name;
                  if (winnerName === teamA) {
                    standings[teamA].won++; standings[teamA].points += 3;
                    standings[teamB].lost++;
                  } else {
                    standings[teamB].won++; standings[teamB].points += 3;
                    standings[teamA].lost++;
                  }
                }
              });
              const sorted = Object.values(standings).sort((a, b) => b.points - a.points || b.won - a.won);
              return (
                <div>
                  <h4 className="dropin-section-title" style={{ marginBottom: '0.75rem' }}>🏆 Standings</h4>
                  {matchesLoading ? (
                    <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem' }}>Loading…</p>
                  ) : dropInMatches.length === 0 ? (
                    <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '2rem 0' }}>No matches yet — standings will appear once matches are created.</p>
                  ) : sorted.length === 0 ? (
                    <p style={{ color: 'rgba(255,255,255,0.4)', textAlign: 'center', padding: '2rem 0' }}>No completed matches yet.</p>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.15)', color: '#94a3b8' }}>
                          <th style={{ textAlign: 'left', padding: '0.5rem' }}>#</th>
                          <th style={{ textAlign: 'left', padding: '0.5rem' }}>Team</th>
                          <th style={{ textAlign: 'center', padding: '0.5rem' }}>GP</th>
                          <th style={{ textAlign: 'center', padding: '0.5rem' }}>W</th>
                          <th style={{ textAlign: 'center', padding: '0.5rem' }}>L</th>
                          <th style={{ textAlign: 'center', padding: '0.5rem' }}>D</th>
                          <th style={{ textAlign: 'center', padding: '0.5rem' }}>Pts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sorted.map((t, i) => (
                          <tr key={t.name} style={{ borderBottom: '1px solid rgba(255,255,255,0.07)', color: i === 0 ? '#facc15' : '#e2e8f0' }}>
                            <td style={{ padding: '0.5rem' }}>{i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}</td>
                            <td style={{ padding: '0.5rem', fontWeight: 600, color: TEAM_COLORS[i % TEAM_COLORS.length] }}>{t.name}</td>
                            <td style={{ textAlign: 'center', padding: '0.5rem' }}>{t.played}</td>
                            <td style={{ textAlign: 'center', padding: '0.5rem', color: '#4ade80' }}>{t.won}</td>
                            <td style={{ textAlign: 'center', padding: '0.5rem', color: '#f87171' }}>{t.lost}</td>
                            <td style={{ textAlign: 'center', padding: '0.5rem', color: '#94a3b8' }}>{t.drawn}</td>
                            <td style={{ textAlign: 'center', padding: '0.5rem', fontWeight: 700 }}>{t.points}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })()}

            {/* ── Messages tab ───────────────────────────────────── */}
            {activeTab === 'messages' && (
              <div>
                <h4 className="dropin-section-title" style={{ marginBottom: '0.75rem' }}>💬 Messages</h4>

                {/* Message list */}
                <div style={{
                  maxHeight: '420px', overflowY: 'auto',
                  background: 'rgba(0,0,0,0.2)',
                  borderRadius: 10, padding: '0.75rem',
                  marginBottom: '1rem',
                  display: 'flex', flexDirection: 'column', gap: '0.5rem',
                }}>
                  {messagesLoading ? (
                    <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.85rem', textAlign: 'center' }}>Loading…</p>
                  ) : messages.length === 0 ? (
                    <p style={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', fontSize: '0.85rem', padding: '1.5rem 0' }}>
                      No messages yet. Be the first!
                    </p>
                  ) : messages.map((msg) => {
                    const isAnnouncement = msg.message_type === 'host_announcement';
                    const isMe = msg.user_id === userId;
                    return (
                      <div
                        key={msg.id}
                        style={{
                          padding: '0.6rem 0.8rem', borderRadius: 8,
                          background: isAnnouncement
                            ? 'rgba(251,191,36,0.1)'
                            : isMe
                              ? 'rgba(167,139,250,0.15)'
                              : 'rgba(255,255,255,0.05)',
                          border: isAnnouncement
                            ? '1px solid rgba(251,191,36,0.3)'
                            : isMe
                              ? '1px solid rgba(167,139,250,0.3)'
                              : '1px solid rgba(255,255,255,0.07)',
                          alignSelf: isMe ? 'flex-end' : 'flex-start',
                          maxWidth: '85%',
                        }}
                      >
                        {isAnnouncement && (
                          <div style={{ fontSize: '0.68rem', color: '#fbbf24', fontWeight: 700, marginBottom: '0.2rem' }}>
                            📢 ANNOUNCEMENT
                          </div>
                        )}
                        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', marginBottom: '0.2rem' }}>
                          {isMe ? 'You' : (msg.user_name || 'Unknown')} · {new Date(msg.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}
                        </div>
                        <div style={{ color: '#e2e8f0', fontSize: '0.875rem' }}>{msg.message}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Compose */}
                {userId && (isHost || (myStatus && myStatus !== 'withdrawn' && myStatus !== 'invited')) ? (
                  <div>
                    <textarea
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      placeholder="Type a message… (Enter to send, Shift+Enter for new line)"
                      rows={2}
                      style={{
                        width: '100%',
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.15)',
                        borderRadius: 8, padding: '0.6rem 0.75rem',
                        color: '#e2e8f0', fontSize: '0.875rem', resize: 'vertical',
                        outline: 'none', boxSizing: 'border-box',
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          if (messageInput.trim()) handleSendMessage('message');
                        }
                      }}
                    />
                    <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                      <button
                        className="dropin-btn dropin-btn--primary"
                        style={{ flex: 1 }}
                        onClick={() => handleSendMessage('message')}
                        disabled={messageSending || !messageInput.trim()}
                      >
                        Send
                      </button>
                      {isHost && (
                        <button
                          className="dropin-btn dropin-btn--warning"
                          style={{ flex: 1 }}
                          onClick={() => handleSendMessage('host_announcement')}
                          disabled={messageSending || !messageInput.trim()}
                        >
                          📢 Announce
                        </button>
                      )}
                    </div>
                  </div>
                ) : userId ? (
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', textAlign: 'center' }}>
                    Join the drop-in to send messages.
                  </p>
                ) : (
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', textAlign: 'center' }}>
                    Sign in to send messages.
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="dropin-action-panel">
            <h4 className="dropin-section-title" style={{ margin: '0 0 0.5rem' }}>Actions</h4>

            {/* Join / Leave / Invite response — for non-host */}
            {userId && !isHost && status !== 'cancelled' && status !== 'completed' && (
              <>
                {/* ── Pending invitation ──────────────────────────────────── */}
                {myStatus === 'invited' && (
                  <>
                    <div style={{
                      background: 'rgba(167,139,250,0.12)',
                      border: '1px solid rgba(167,139,250,0.35)',
                      borderRadius: 10,
                      padding: '0.7rem 1rem',
                      marginBottom: '0.5rem',
                      textAlign: 'center',
                    }}>
                      <p style={{ color: '#c4b5fd', fontWeight: 700, margin: '0 0 0.2rem', fontSize: '0.9rem' }}>
                        📬 You've been invited!
                      </p>
                      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78rem', margin: 0 }}>
                        The host has invited you to this drop-in.
                      </p>
                    </div>
                    <button
                      className="dropin-btn dropin-btn--success"
                      onClick={handleAcceptInvite}
                      disabled={actionLoading}
                    >
                      ✅ Accept Invitation
                    </button>
                    <button
                      className="dropin-btn dropin-btn--danger"
                      onClick={handleRejectInvite}
                      disabled={actionLoading}
                    >
                      ✕ Decline
                    </button>
                  </>
                )}

                {/* The host's refund policy, read before paying */}
                {price && <RefundPolicyNote policy={d.refundPolicy ?? d.refund_policy} />}

                {/* ── Not yet joined ──────────────────────────────────────── */}
                {(!myStatus || myStatus === 'withdrawn') && (
                  activePlayers >= maxPlayers && (maxWaitlist === 0 || waitlisted.length >= maxWaitlist) ? (
                    <button className="dropin-btn dropin-btn--secondary" disabled>
                      🚫 Session Full
                    </button>
                  ) : (
                    <button
                      className="dropin-btn dropin-btn--success"
                      onClick={handleJoin}
                      disabled={actionLoading}
                    >
                      {activePlayers >= maxPlayers ? '⏳ Join Waitlist' : price ? `✅ Join and pay ${price}` : '✅ Join Drop-in'}
                    </button>
                  )
                )}

                {/* ── Already a member (confirmed / waitlisted / checked_in) */}
                {myStatus && myStatus !== 'withdrawn' && myStatus !== 'invited' && (
                  <>
                    {iOwe && (
                      <button
                        className="dropin-btn dropin-btn--success"
                        onClick={handlePay}
                        disabled={actionLoading}
                      >
                        💳 Pay {price} to confirm
                      </button>
                    )}
                    <button
                      className="dropin-btn dropin-btn--danger"
                      onClick={handleLeave}
                      disabled={actionLoading}
                    >
                      Leave Drop-in
                    </button>
                    <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.65)', textAlign: 'center' }}>
                      You are {myStatus === 'waitlisted' ? 'on the waitlist' : iOwe ? 'holding a spot: pay to keep it' : `confirmed (${myStatus})`}
                      {price && myStatus === 'waitlisted' ? `. You pay ${price} once a spot opens.` : ''}
                    </p>
                  </>
                )}
              </>
            )}

            {/* Host controls */}
            {isHost && (
              <>
                {price && (
                  <PaidSummary
                    statuses={[...confirmed, ...checkedIn].filter((p) => p.user_id !== userId).map((p) => p.payment_status)}
                    note="An unpaid spot is held for 30 minutes, then given to the next player, and the player is told."
                  />
                )}
                {price && <HostPaymentsLink />}
                {/* Recurring template: open this week's session */}
                {(d.isRecurring || d.is_recurring) && (
                  <button
                    className="dropin-btn dropin-btn--primary"
                    onClick={handleOpenWeekly}
                    disabled={weeklyOpening}
                  >
                    📅 {weeklyOpening ? 'Opening…' : "Open This Week's Session"}
                  </button>
                )}

                {status === 'open' && (
                  <button
                    className="dropin-btn dropin-btn--warning"
                    onClick={() => doAction(() => dropInService.start(id))}
                    disabled={actionLoading}
                  >
                    🏁 Start Session
                  </button>
                )}

                {(status === 'open' || status === 'in_progress') && activePlayers >= 2 && (
                  <button
                    className="dropin-btn dropin-btn--primary"
                    onClick={() => setActiveTab('teams')}
                    disabled={actionLoading}
                  >
                    ⚽ Manage Teams
                  </button>
                )}

                {status === 'in_progress' && (
                  <button
                    className="dropin-btn dropin-btn--secondary"
                    onClick={() => doAction(() => dropInService.complete(id))}
                    disabled={actionLoading}
                  >
                    🏆 Complete Session
                  </button>
                )}

                {status !== 'cancelled' && status !== 'completed' && (
                  <>
                    <button
                      className="dropin-btn dropin-btn--secondary"
                      onClick={() => navigate(`/drop-in/${id}/edit`)}
                      disabled={actionLoading}
                    >
                      ✏️ Edit Drop-in
                    </button>
                    <button
                      className="dropin-btn dropin-btn--danger"
                      onClick={() => doAction(() => dropInService.cancel(id))}
                      disabled={actionLoading}
                    >
                      🗑️ Cancel Drop-in
                    </button>

                    {/* Invite player */}
                    <div style={{ marginTop: '0.5rem' }}>
                      <label className="form-label-gaming" style={{ marginBottom: '0.3rem', display: 'block' }}>
                        📨 Invite Player
                      </label>
                      <div style={{ display: 'flex', gap: '0.4rem' }}>
                        <input
                          type="text"
                          value={inviteSearch}
                          onChange={(e) => handleInviteSearch(e.target.value)}
                          placeholder="Search by name or username"
                          className="dropin-filter-select"
                          style={{ flex: 1, borderRadius: 8 }}
                        />
                      </div>
                      {inviteSearching && (
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.25rem' }}>Searching…</p>
                      )}
                      {inviteResults.length > 0 && (
                        <div style={{
                          marginTop: '0.4rem',
                          background: 'rgba(15,23,42,0.95)',
                          border: '1px solid rgba(255,255,255,0.15)',
                          borderRadius: 10,
                          overflow: 'hidden',
                        }}>
                          {inviteResults.map((u) => (
                            <div
                              key={u.id}
                              style={{
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                padding: '0.5rem 0.75rem',
                                borderBottom: '1px solid rgba(255,255,255,0.07)',
                                fontSize: '0.8rem',
                              }}
                            >
                              <span style={{ color: 'rgba(255,255,255,0.85)' }}>
                                {u.display_name}
                                {u.username && <span style={{ color: 'rgba(255,255,255,0.45)', marginLeft: '0.4rem' }}>@{u.username}</span>}
                              </span>
                              <button
                                className="dropin-btn dropin-btn--primary"
                                style={{ width: 'auto', padding: '0.2rem 0.7rem', fontSize: '0.75rem' }}
                                onClick={() => handleInvitePlayer(u.id)}
                                disabled={actionLoading}
                              >
                                + Invite
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                      {inviteSearch.length >= 2 && !inviteSearching && inviteResults.length === 0 && (
                        <p style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.4)', marginTop: '0.25rem' }}>No users found</p>
                      )}
                    </div>
                  </>
                )}
              </>
            )}

            {/* Notes */}
            {(d.notes) && (
              <div style={{
                marginTop: '0.5rem',
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 10,
                padding: '0.75rem',
                fontSize: '0.8rem',
                color: '#94a3b8',
              }}>
                <strong style={{ color: '#cbd5e1' }}>Notes:</strong><br />{d.notes}
              </div>
            )}
          </div>
        </div>

        {/* Leave confirmation, with what happens to a payment */}
        <ConfirmModal
          isOpen={leaveOpen}
          title="Leave drop-in"
          message={leaveMessage({
            what: 'this drop-in',
            paid: myRecord?.payment_status === 'paid',
            started: status === 'in_progress' || new Date(d.scheduledAt || d.scheduled_at).getTime() <= Date.now(),
            policy: d.refundPolicy ?? d.refund_policy,
            rejoin: true,
          })}
          confirmText="Leave"
          cancelText="Stay"
          isDanger
          isLoading={actionLoading}
          onConfirm={confirmLeave}
          onCancel={() => setLeaveOpen(false)}
        />

        {/* Remove player confirmation */}
        <ConfirmModal
          isOpen={removeConfirm.isOpen}
          title="Remove Player"
          message={`Are you sure you want to remove ${removeConfirm.playerName || 'this player'} from the drop-in?`}
          confirmText="Remove"
          cancelText="Cancel"
          isDanger
          onConfirm={confirmRemovePlayer}
          onCancel={() => setRemoveConfirm({ isOpen: false, playerId: null, playerName: '' })}
        />
      </div>
    </GameLayout>
  );
};

export default DropInDetails;
